import { connect } from 'react-redux';
import { List } from 'immutable';
import moment from 'moment';
import { toggleModal } from '@/actions';
import {
  setProviderSortOrder,
  setProviderFilterBy,
  resetProviderFilters,
} from '@/actions/providers';
import { setTripFilter } from '@/actions/tripFilters';
import parseQueryString from 'utils/queryString/parseQueryString';
import getTransportState from 'utils/search/getTransportState';
import mergeProviders from 'utils/providers/mergeProviders';
import stringifyQueryString from 'utils/queryString/stringifyQueryString';
import findTrip from 'utils/trips/findTrip';
import { formatDuration } from 'utils/Helpers';
import { generateDepartureUrl } from 'utils/urls';
import {
  filterByTransport,
  filterByOpenProvider,
  filterByTransporter,
  retrieveOpenTrip,
} from 'utils/providers/filterProviders';
import Providers from './Providers';

export const mapStateToProps = (state, ownProps) => {
  const {
    config,
    search,
    providers,
    cities,
    terminals,
    overlayView,
    trips,
    whitelabelConfig: { features },
  } = state;
  const { match, location } = ownProps;
  const places = { ...cities, ...terminals };
  const { params, url: currentUrl } = match;
  const {
    departureDate,
    returnDate,
    passengers = 'A1',
    departureId,
    way = 'departure',
    originSlug,
    destinationSlug,
  } = params;
  const { adAttribution, returnIsOpenTicket } = parseQueryString(location.search);
  const dSearch = search.get('departure') || List([]);
  const rSearch = search.get('return') || List([]);
  const dTransportState = providers.getIn([dSearch.get('id'), 'state']);
  const rTransportState = providers.getIn([rSearch.get('id'), 'state']);
  const transportState = getTransportState(dTransportState, rTransportState, !!returnDate, way);
  const dProviders = providers.getIn([dSearch.get('id'), 'list']);
  const rProviders = providers.getIn([rSearch.get('id'), 'list']);
  const showing = providers.get('showing');
  const mergedProviders = mergeProviders(dProviders, rProviders, way, !!returnDate);

  // Check if search state matches current route params - if not, data is stale
  const searchOriginId = dSearch.get('originId');
  const searchDestinationId = dSearch.get('destinationId');
  const isSearchStale = searchOriginId !== originSlug || searchDestinationId !== destinationSlug;

  let openTrip;
  if (features.SHOW_OPEN_TICKET_ON_PROVIDERS && !returnIsOpenTicket) {
    const searchId = way === 'departure' ? dSearch.get('id') : rSearch.get('id');
    const tripList = trips.getIn([searchId, 'buses', 'trips']);
    openTrip = retrieveOpenTrip(tripList);
  }

  let filteredProviders = filterByTransport(mergedProviders, showing);

  // On the return leg we look up the selected departure trip to (optionally) scope the provider
  // list and to render the "selected departure" summary + return days carousel on the picker.
  const isReturnPicker = way === 'return' && Boolean(departureId);
  const departureTrip = isReturnPicker
    ? findTrip(trips.get(dSearch.get('id')), 'bus', departureId)?.toJS()
    : undefined;

  // Scope the return provider list to the departure transporter group only when cross-line round
  // trips are enabled AND any-line round trips are NOT (any-line takes precedence: offer all lines).
  if (
    isReturnPicker &&
    !features.ALLOW_ANY_LINE_ROUND_TRIPS &&
    features.ALLOW_CROSS_LINE_ROUND_TRIPS
  ) {
    const departureTransporterName = departureTrip?.line?.transporterName;
    if (departureTransporterName) {
      filteredProviders = filterByTransporter(filteredProviders, departureTransporterName);
    }
  }

  // Summary of the already-selected departure trip, shown above the return provider picker so the
  // round-trip context ("selected departure" + "change departure") matches the schedule screen.
  // Gated behind ALLOW_ANY_LINE_ROUND_TRIPS so tenants that don't opt into any-line round trips
  // keep the current picker unchanged.
  const showDepartureTripPicked =
    isReturnPicker && Boolean(departureTrip) && Boolean(features.ALLOW_ANY_LINE_ROUND_TRIPS);
  const departureMoment = showDepartureTripPicked && moment(departureTrip.departure);
  const departurePickedInfo = showDepartureTripPicked
    ? {
        tripDate: departureMoment.format('ddd DD MMMM'),
        tripTime: departureMoment.format('LT'),
        tripDepartureDuration: formatDuration(departureTrip.duration),
        originCity: departureTrip.origin?.cityName,
        destinationCity: departureTrip.destination?.cityName,
        departureLineLogo: departureTrip.line?.logoUrl,
        isDepartureOpenTicket: Boolean(departureTrip.openTicket),
      }
    : {};
  if (features.SHOW_OPEN_TICKET_ON_PROVIDERS && returnIsOpenTicket && way === 'return') {
    const searchId = way === 'departure' ? dSearch.get('id') : rSearch.get('id');
    const tripList = trips.getIn([searchId, 'buses', 'trips']);
    filteredProviders = filterByOpenProvider(filteredProviders, tripList);
  }

  // Only use uniqueLine if search state is not stale
  const uniqueLine =
    !isSearchStale && filteredProviders.size === 1 ? filteredProviders.getIn([0, 'id']) : '';

  return {
    currentPath: location.pathname,
    currentUrl,
    passengers,
    way,
    departureId,
    roundTrip: !!returnDate,
    departureDate,
    returnDate,
    adAttribution,
    origin: places[dSearch.get('originId')],
    destination: places[dSearch.get('destinationId')],
    installmentsMinAmount: config.installmentsMinAmount,
    providers: filteredProviders.toJS(),
    noResults: transportState.valueSeq().every((tState) => tState === 'disabled'),
    isLoading: transportState.valueSeq().some((tState) => tState === 'pending'),
    sortBy: providers.get('sortBy'),
    filterBy: providers.get('filterBy'),
    providerSortVisible: overlayView.get('providerSortVisible'),
    uniqueLine,
    isOpenTicket: returnIsOpenTicket,
    openTrip,
    showDepartureTripPicked,
    ...departurePickedInfo,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  showModal: (component, active) => dispatch(toggleModal('visible', component, { active })),

  sortProvidersBy: (sortBy) => dispatch(setProviderSortOrder(sortBy)),

  filterProvidersBy: (filterBy) => dispatch(setProviderFilterBy(filterBy)),

  setTripFilter: (filterBy, active) => dispatch(setTripFilter(filterBy, active)),

  resetFilters: () => dispatch(resetProviderFilters()),

  transitionTo: (route, searchQuery) => {
    const queryString = stringifyQueryString(searchQuery, ownProps.location.search);

    ownProps.history.push(`${route}${queryString}`);
  },

  next: (route, searchQuery) => {
    const queryString = stringifyQueryString(searchQuery, ownProps.location.search);

    ownProps.history.push(`${route}${queryString}`);
  },

  skip: (route, searchQuery) => {
    const queryString = stringifyQueryString(searchQuery, ownProps.location.search);
    return ownProps.history.replace(`${route}${queryString}`);
  },

  redirectToDeparture: (hasProviderSelection) => {
    const { originSlug, destinationSlug, returnDate, departureDate, passengers } =
      ownProps.match.params;
    ownProps.history.push(
      generateDepartureUrl({
        originSlug,
        destinationSlug,
        departureDate,
        returnDate,
        passengers,
        useProviderSelection: hasProviderSelection,
      }),
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Providers);
